import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react"
import { useId } from "react"

import { cx } from "../lib/cx"
import { Button } from "./button"
import styles from "./field.module.css"

/**
 * Поля ввода.
 *
 * ГЛАВНОЕ ЗДЕСЬ — СВЯЗКА, А НЕ ВИД. Подпись, пояснение и ошибка должны быть привязаны
 * к самому полю: человек, который слушает экран, обязан услышать «Телефон клиента,
 * обязательное, Нужен номер целиком» одним куском, а не искать текст рядом глазами.
 * Поэтому идентификаторы выдаются здесь, а не вызывающим кодом: забытый aria-describedby
 * не виден на экране и потому не будет замечен ни на одной приёмке.
 */

interface FieldParts {
  readonly baseId: string
  readonly controlId: string
  readonly describedBy: string | undefined
  readonly invalid: boolean
}

function useFieldParts(hint: string | undefined, error: string | undefined): FieldParts {
  const base = useId()
  const ids: string[] = []
  if (typeof hint === "string" && hint.length > 0) ids.push(`${base}-hint`)
  if (typeof error === "string" && error.length > 0) ids.push(`${base}-error`)
  return {
    baseId: base,
    controlId: `${base}-control`,
    describedBy: ids.length === 0 ? undefined : ids.join(" "),
    invalid: typeof error === "string" && error.length > 0,
  }
}

export interface FieldShellProps {
  readonly label: string
  readonly required?: boolean
  readonly hint?: string | undefined
  readonly error?: string | undefined
  readonly className?: string | undefined
}

/**
 * Обёртка поля: подпись, звёздочка, пояснение, ошибка. Вынесена отдельно, потому что
 * маскированное поле и пара «поле + кнопка» пользуются ею так же, как обычный ввод.
 */
function FieldShell(
  props: FieldShellProps & {
    readonly controlId: string
    readonly baseId: string
    readonly children: ReactNode
  },
): ReactNode {
  const { label, required = false, hint, error } = props
  const hasHint = typeof hint === "string" && hint.length > 0
  const hasError = typeof error === "string" && error.length > 0

  return (
    <div className={cx(styles.group, props.className)}>
      <label className={styles.label} htmlFor={props.controlId}>
        {label}
        {required ? (
          <>
            {" "}
            {/* Звёздочка — знак, а не слово: рядом кладётся её расшифровка для чтения
                с экрана, иначе поле просто «Телефон клиента», и обязательность теряется. */}
            <span className={styles.required} aria-hidden="true">
              *
            </span>
            <span className="plnr-visually-hidden">обязательное поле</span>
          </>
        ) : null}
      </label>

      {props.children}

      {hasHint ? (
        <small className={styles.hint} id={`${props.baseId}-hint`}>
          {hint}
        </small>
      ) : null}

      {hasError ? (
        /*
         * Ошибка поля НЕ помечена role="alert". Она уже привязана через aria-describedby
         * и aria-invalid, и человек услышит её, когда дойдёт до поля. role="alert" прерывал бы
         * чтение формы посреди слова при каждом нажатии клавиши в соседнем поле.
         */
        <span className={styles.error} id={`${props.baseId}-error`}>
          {error}
        </span>
      ) : null}
    </div>
  )
}

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "id" | "className" | "aria-describedby" | "aria-invalid" | "required"
>

export interface TextFieldProps extends FieldShellProps, NativeInputProps {}

export function TextField(props: TextFieldProps): ReactNode {
  const { label, required = false, hint, error, className, ...input } = props
  const parts = useFieldParts(hint, error)

  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      controlId={parts.controlId}
      baseId={parts.baseId}
    >
      <input
        {...input}
        id={parts.controlId}
        className={styles.control}
        required={required}
        aria-describedby={parts.describedBy}
        aria-invalid={parts.invalid ? true : undefined}
      />
    </FieldShell>
  )
}

type NativeTextAreaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "id" | "className" | "aria-describedby" | "aria-invalid" | "required"
>

export interface TextAreaFieldProps extends FieldShellProps, NativeTextAreaProps {}

/**
 * Многострочное поле — та же связка подписи, пояснения и ошибки, что у TextField.
 * Высота растёт рукой (resize: vertical), а не сама: поле, прыгающее при наборе, сдвигает
 * под пальцем кнопку «Сохранить».
 */
export function TextAreaField(props: TextAreaFieldProps): ReactNode {
  const { label, required = false, hint, error, className, ...input } = props
  const parts = useFieldParts(hint, error)

  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      controlId={parts.controlId}
      baseId={parts.baseId}
    >
      <textarea
        {...input}
        id={parts.controlId}
        className={cx(styles.control, styles.textarea)}
        required={required}
        aria-describedby={parts.describedBy}
        aria-invalid={parts.invalid ? true : undefined}
      />
    </FieldShell>
  )
}

type NativeSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "id" | "className" | "aria-describedby" | "aria-invalid" | "required" | "children"
>

export interface SelectFieldProps extends FieldShellProps, NativeSelectProps {
  readonly options: readonly { readonly value: string; readonly label: string }[]
}

export function SelectField(props: SelectFieldProps): ReactNode {
  const { label, required = false, hint, error, className, options, ...select } = props
  const parts = useFieldParts(hint, error)

  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      controlId={parts.controlId}
      baseId={parts.baseId}
    >
      <div className={styles.selectWrap}>
        <select
          {...select}
          id={parts.controlId}
          className={cx(styles.control, styles.select)}
          required={required}
          aria-describedby={parts.describedBy}
          aria-invalid={parts.invalid ? true : undefined}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* Стрелка своя: системная в тёмной теме остаётся светлой и выглядит чужой.
            Рисуется currentColor, поэтому переворачивается вместе с темой сама. */}
        <svg
          className={styles.selectArrow}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
    </FieldShell>
  )
}

export interface MaskedFieldProps extends FieldShellProps {
  /** Что показывать, пока значение скрыто: «+7 916 ••• •• 67». */
  readonly masked: string
  /** Полное значение. Приходит только тогда, когда сервер его отдал. */
  readonly revealed?: string | undefined
  readonly revealLabel: string
  /** Чем это значение является словами — для чтения с экрана вместо ряда точек. */
  readonly spoken: string
  readonly onReveal?: (() => void) | undefined
  readonly revealing?: boolean
  readonly deniedReason?: string | undefined
}

/**
 * Маскированное значение с кнопкой «Показать».
 *
 * Поле только для чтения: открыть номер целиком — это действие, которое пишется
 * в журнал действий (подпись под полем в макете), поэтому оно идёт через кнопку,
 * а не через выделение мышью. Ряд точек «•••» программа чтения с экрана произносит
 * как набор маркеров, поэтому рядом лежит словесная расшифровка.
 */
export function MaskedField(props: MaskedFieldProps): ReactNode {
  const { label, required = false, hint, error, className } = props
  const parts = useFieldParts(hint, error)
  const isRevealed = typeof props.revealed === "string" && props.revealed.length > 0
  const shown = isRevealed ? (props.revealed ?? props.masked) : props.masked

  return (
    <FieldShell
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      controlId={parts.controlId}
      baseId={parts.baseId}
    >
      <div className={styles.maskedRow}>
        <input
          id={parts.controlId}
          className={cx(styles.control, styles.masked)}
          value={shown}
          readOnly
          aria-describedby={parts.describedBy}
          aria-label={isRevealed ? undefined : props.spoken}
          onChange={() => {
            // Значение приходит сверху; поле только показывает. Обработчик нужен React,
            // иначе он ругается на управляемое поле без onChange.
          }}
        />
        {isRevealed ? null : (
          <Button
            variant="secondary"
            size="sm"
            loading={props.revealing === true}
            deniedReason={props.deniedReason}
            onClick={props.onReveal}
          >
            {props.revealLabel}
          </Button>
        )}
      </div>
    </FieldShell>
  )
}

export function Checkbox(props: {
  readonly children: ReactNode
  readonly checked?: boolean
  readonly defaultChecked?: boolean
  readonly disabled?: boolean
  readonly onChange?: ((checked: boolean) => void) | undefined
  readonly className?: string | undefined
}): ReactNode {
  return (
    <label className={cx(styles.checkRow, props.className)}>
      <input
        type="checkbox"
        className={styles.checkbox}
        checked={props.checked}
        defaultChecked={props.defaultChecked}
        disabled={props.disabled}
        onChange={(event) => props.onChange?.(event.currentTarget.checked)}
      />
      <span>{props.children}</span>
    </label>
  )
}

/**
 * Выключатель.
 *
 * Это флажок с role="switch", а не кнопка с aria-pressed: у него два УСТОЙЧИВЫХ
 * положения («согласие на рекламу получено» / «не получено»), он живёт в форме
 * и отправляется вместе с ней. Родной тег даёт пробел, Tab и правильное объявление
 * положения бесплатно.
 */
export function Switch(props: {
  readonly children: ReactNode
  readonly checked?: boolean
  readonly defaultChecked?: boolean
  readonly disabled?: boolean
  readonly onChange?: ((checked: boolean) => void) | undefined
  readonly className?: string | undefined
}): ReactNode {
  return (
    <label className={cx(styles.checkRow, props.className)}>
      <input
        type="checkbox"
        role="switch"
        className={styles.switch}
        checked={props.checked}
        defaultChecked={props.defaultChecked}
        disabled={props.disabled}
        onChange={(event) => props.onChange?.(event.currentTarget.checked)}
      />
      <span>{props.children}</span>
    </label>
  )
}
